import { Fragment, cloneElement, isValidElement, useEffect, useLayoutEffect, useRef, useState, type InputHTMLAttributes, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import type { ListResult } from '../lib/api';
import { MIN_COL_WIDTH, applyOrder, colWidthStyle, moveKey, useTablePrefs } from '../lib/tableCols';
import { usePhone } from '../lib/phone';
import { TopScrollbar } from './TopScrollbar';
import { COPY_HINT, armCopy, copyMessage, copyToast } from '../lib/cellCopy';
import { useFitHeight } from '../lib/fitHeight';
import { usePageRefresh } from '../lib/pageRefresh';
import { useOwnerPick } from '../lib/ownerPick';

export type Row = Record<string, unknown>;

/** 이보다 줄이 많으면 보이는 줄만 그린다(#241) · 앞뒤로 더 그려 둘 줄 수. */
export const VIRT_MIN = 300;
export const VIRT_OVER = 20;

export interface Column {
  key: string;
  /** 문자열이 보통이지만 전체선택 체크박스처럼 머리글이 요소일 때가 있다. */
  label: ReactNode;
  align?: 'right';
  render?: (row: Row) => ReactNode;
  /**
   * 합계 칸 서식. 없으면 `key` 와 같은 이름의 합계를 금액으로 찍는다.
   * 한 칸에 두 값을 넣는 컬럼(예: `배정/필요`)은 여기서 직접 조립한다 —
   * 기본 서식으로는 둘 중 하나만 나와 **머리글과 뜻이 어긋난다.**
   */
  totalRender?: (values: Record<string, unknown>) => ReactNode;
  /**
   * 숨길 수 없는 칼럼. 그 칸이 **표의 기준**일 때만 쓴다 —
   * 예: 청구 상세의 '노선' 칸은 배정 차량 정보까지 품고 있어 숨기면 차량 줄이 통째로 빈다.
   */
  required?: boolean;
  /** 정렬할 수 없는 칸(서버 화이트리스트 밖 · 버튼 칸). `onSort` 가 있어도 머리글을 누를 수 없게 둔다. */
  noSort?: boolean;
  /**
   * 폰(md 미만)에서만: `hide` = 기본으로 뺀다(번호·보조 숫자처럼 폰에서 밀어 볼 가치가 낮은 칸) ·
   * `pin` = 맨 앞으로 옮겨 **왼쪽에 고정**(옆으로 밀어도 어느 줄인지 보이게 — 거래처·기사 이름). 표마다 pin 은 하나만.
   * PC 는 무시한다(2026-09-30 way 「추천대로」 — 금액 칸이 오른쪽이라 폰에서 밀어야 보였다).
   */
  phone?: 'hide' | 'pin';
  /**
   * 누르면 그 칸 값을 고르고 Ctrl+C 로 복사(#250) · `dragSum` 표는 세로로 끌어 고른 칸을 줄바꿈으로 이어 복사(#172 · 엑셀처럼) · 옆 칸까지 끌면 네모로(칸 사이는 탭).
   * 빈 문자열을 돌려주는 칸은 고르지 않는다. 고른 칸만 칠해지고 표 글자는 잡히지 않는다.
   */
  copy?: (row: Row) => string;
}

/** 비어 있는 값은 화면에서 구분되게 '—' 로. 빈 문자열도 값 없음으로 본다. */
export const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

/**
 * 검색어 강조(#188 · 구 버스인처럼 걸린 글자를 노랗게). 칸의 글자에서 `terms` 를 대소문자 없이 찾아 `<mark>` 로 감싼다.
 * `render` 가 요소를 돌려줘도 그 안의 글자까지 내려가 감싼다(차량번호 링크·흐린 글씨 등).
 */
export function markTerms(node: ReactNode, terms: string[]): ReactNode {
  if (!terms.length || node === null || node === undefined || typeof node === 'boolean') return node;
  if (typeof node === 'string' || typeof node === 'number') {
    const s = String(node);
    const low = s.toLowerCase();
    const parts: ReactNode[] = [];
    let i = 0;
    while (i < s.length) {
      let at = -1;
      let len = 0;
      for (const t of terms) {
        const j = low.indexOf(t, i);
        if (j >= 0 && (at < 0 || j < at || (j === at && t.length > len))) { at = j; len = t.length; }
      }
      if (at < 0) break;
      if (at > i) parts.push(s.slice(i, at));
      parts.push(<mark key={at} className="rounded-sm bg-yellow-200 px-0 text-inherit">{s.slice(at, at + len)}</mark>);
      i = at + len;
    }
    if (!parts.length) return node;
    if (i < s.length) parts.push(s.slice(i));
    return <>{parts}</>;
  }
  if (Array.isArray(node)) return node.map((n, k) => <Fragment key={k}>{markTerms(n, terms)}</Fragment>);
  if (isValidElement<{ children?: ReactNode }>(node) && node.props.children !== undefined) {
    return cloneElement(node, undefined, markTerms(node.props.children, terms));
  }
  return node;
}

/** 검색창 글자 → 강조할 낱말(공백으로 나눔 · 소문자). */
export const highlightTerms = (q: string | null | undefined) =>
  String(q ?? '').toLowerCase().split(/\s+/).filter(Boolean);

/**
 * 번호 칸(운행편성·기사·차주 ID — 2026-09-30 way 「지급·공제 화면엔 누구에게 가는지 정확히」).
 * `warn` 이면 비었을 때 빨갛게 — 편성 번호가 없는 지급·공제 줄은 받는 사람이 불분명하다는 뜻이다.
 */
export function IdCell({ v, warn }: { v: unknown; warn?: boolean }) {
  if (v === null || v === undefined || v === '') {
    return warn
      ? <span className="text-xs font-medium text-red-600" title="차량·기사·차주·소속이 모두 같은 운행편성이 없습니다 — 받는 사람을 확인하세요.">없음</span>
      : <span className="text-xs text-ink-faint">—</span>;
  }
  return <span className="text-xs tabular-nums text-ink-soft">{String(v)}</span>;
}

/**
 * 금액 표시. DB 의 numeric 은 pg 가 **문자열**로 준다(반올림 사고를 막으려는 의도).
 * 여기서 Number 로 바꾸는 건 **표시 서식용**이다 — 이 값으로 계산하지 말 것.
 */
/**
 * 금액을 **입력칸에 넣을 문자열**로.
 *
 * pg numeric 은 `168000.0000` 처럼 자릿수를 채워 준다. 그대로 넣으면 사용자가 고치기 전에
 * 먼저 `.0000` 을 지워야 한다(2026-09-17 사용자: "모든 금액은 소숫점자리가 필요없음").
 *
 * 🔴 **반올림하지 않는다 — 뒤의 0 만 턴다.** 레거시에 진짜 소수가 있고(예 `…123.727`),
 *    반올림해 두면 사용자가 **손도 안 대고 저장만 해도 값이 조용히 바뀐다.**
 *    (보여주기용 반올림은 `money` 가 한다. 이건 **되돌려 보낼 값**이라 성격이 다르다.)
 * ⚠️ 소수점이 없으면 손대지 않는다 — 안 그러면 `1000` 이 `1` 이 된다.
 */
export const moneyInput = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).trim();
  if (!/^-?[0-9]+(\.[0-9]+)?$/.test(s)) return s; // 모르는 모양은 그대로 둔다
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
};

/**
 * 금액 입력칸 표시용 천 단위 쉼표(#23 「45000 → 45,000」). **문자열로만** 다룬다 — Number 왕복은 소수·큰 수를 깎는다.
 * 입력 중인 값(`-`·끝의 `.`)도 그대로 살린다. 보낼 때는 `uncomma` 로 벗긴다.
 */
export const commaInput = (v: string): string => {
  const s = v.replace(/,/g, '');
  const m = /^(-?)([0-9]*)(\.[0-9]*)?$/.exec(s);
  if (!m) return v;
  return `${m[1]}${(m[2] ?? '').replace(/\B(?=([0-9]{3})+(?![0-9]))/g, ',')}${m[3] ?? ''}`;
};
export const uncomma = (v: string): string => v.replace(/,/g, '');

/**
 * 금액 입력칸(2026-09-30 way 「모든 금액 천 단위 쉼표 · 소수점 필요 없음」).
 * `value` 는 **원값**(쉼표 없음, 서버에서 온 `2454545.4545` 그대로)이고 칸에는 원 단위 반올림 + 쉼표로 보인다.
 * 🔴 손대지 않으면 부모의 원값이 그대로 저장된다 — 보이는 대로 반올림해 보내면 열어서 저장만 해도 금액이 바뀐다.
 * 고치면 정수(원)로 바뀐다. `onChange` 는 쉼표 없는 글자를 준다.
 */
export function MoneyInput({ value, onChange, className, ...rest }: {
  value: unknown; onChange: (raw: string) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const raw = uncomma(String(value ?? ''));
  const shown = /^-?[0-9]+(\.[0-9]+)?$/.test(raw) ? commaInput(String(Math.round(Number(raw)))) : raw;
  return (
    <input
      {...rest}
      className={`field text-right tabular-nums ${className ?? ''}`}
      inputMode="numeric"
      value={shown}
      onChange={(e) => onChange(uncomma(e.target.value).replace(/[^0-9-]/g, ''))}
    />
  );
}

export const money = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  // **원 단위로 반올림해서 보여준다**(2026-08-01 사용자 요청). 소수점은 레거시 계산식이
  // 흘린 것이고(예: 3,466,817,123.727) 실제 청구·지급은 원 단위로 이뤄진다.
  // ⚠️ 표시만 반올림한다 — 합계는 여전히 SQL 이 **원값으로** 내므로,
  //    소수가 섞인 달은 보이는 줄을 눈으로 더한 값과 총계가 1~2원 어긋날 수 있다.
  //    그 반대(합계도 반올림값으로 다시 계산)로 가면 총계가 정본에서 멀어진다.
  const r = Math.round(n);
  return (Object.is(r, -0) ? 0 : r).toLocaleString('ko-KR');
};

/** `2026-07-22T09:30:00` 같은 벽시계 문자열에서 분까지만. 타임존 변환을 하지 않는다. */
export const wallClock = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v).replace('T', ' ').slice(0, 16);
};

/**
 * 합계 줄에 쓸 값 묶음.
 *
 * ⚠️ **이 값은 반드시 서버가 낸다.** 화면에 있는 행을 더하면 안 된다 — 목록은 한 페이지
 *    (최대 200행)뿐이라 그 합은 "필터에 걸린 전체"가 아니라 "지금 보이는 것"이고,
 *    둘은 거의 항상 다르다. 그런데 화면에서는 구분이 안 되므로 **틀린 숫자가 맞는 척한다**
 *    (`.claude/issues.md` 65 와 같은 자리 — 목록 상한이 업무 상한이 된 사고).
 *    금액이 문자열인 것도 같은 이유다(numeric 을 JS 숫자로 왕복시키면 소수 4자리가 깎인다).
 */
export interface TotalRow {
  /** 필터 전체의 행 수. 화면에 보이는 행 수와 다르면 그 사실을 같이 보여준다. */
  count: number;
  /** 컬럼 key → 서버가 낸 합계(문자열 그대로). */
  values: Record<string, unknown>;
  /** 왼쪽 첫 칸에 쓸 이름. 기본 '총계'. */
  label?: string;
}

/** 소계 한 줄 — 서버가 그룹별로 낸 합계. `key` 가 그룹을 가르는 값이다. */
export interface SubtotalRow extends TotalRow {
  key: string;
}

/**
 * 접어 보기(#489 통합 청구 묶음) — 같은 `of` 키 줄들 위에 머리 줄 하나(서버가 낸 합계 `rows`)를 세우고,
 * 머리 줄을 누르면 그 줄들을 펴고 접는다. 목록이 **그 줄들을 붙여서** 줘야 한다(서버 정렬).
 * 키가 null 이거나 `rows` 에 없는 줄은 그대로 그린다.
 */
export interface FoldSpec {
  of: (row: Row) => string | null;
  rows: SubtotalRow[];
  open: ReadonlySet<string>;
  toggle: (key: string) => void;
  /** 머리 줄 이름 옆 표시(묶음 배지 등). */
  badge?: (s: SubtotalRow) => ReactNode;
  /** 이름을 넣을 칸(예: 거래처). 그 칸이 숨겨졌거나 없으면 첫 칸에 「+」와 같이. */
  labelKey?: string;
}

/**
 * 서버가 준 납작한 합계 객체(`{count, cost_cust, …}`)를 표가 쓰는 모양으로 바꾼다.
 * `count`·`key` 는 메타라 합계 칸에서 빼고, 나머지를 **컬럼 key 그대로** 넘긴다 —
 * 그래서 서버 컬럼명과 표 컬럼 `key` 가 같으면 저절로 제자리에 선다.
 */
export function toTotal(raw: Record<string, unknown> | null | undefined, label?: string): TotalRow | null {
  if (!raw) return null;
  const { count, key: _key, ...values } = raw as { count?: unknown; key?: unknown };
  return { count: Number(count ?? 0), values: values as Record<string, unknown>, label };
}

export function toSubtotals(raw: Record<string, unknown>[] | null | undefined): SubtotalRow[] {
  return (raw ?? []).map((r) => ({ ...toTotal(r)!, key: String(r.key ?? '') }));
}

export function DataTable({
  columns,
  rows,
  loading,
  onRowClick,
  activeRow,
  rowClass,
  total,
  subtotals,
  groupOf,
  folds,
  tableId,
  totalAt = 'bottom',
  sort,
  onSort,
  filters,
  onFilter,
  dragSum,
  onCopy,
  highlight,
  onRowMouseDown,
  onRowMouseEnter,
  exportXlsx,
  openOnDoubleClick,
  onRowSelect,
}: {
  columns: Column[];
  rows: Row[];
  loading: boolean;
  onRowClick?: (row: Row) => void;
  /**
   * 줄을 **두 번 눌러야** 열기(10-14 #241 정여진 — 기준정보). 한 번 누름은 칸 복사(`copy`) 등에 쓴다.
   * 폰에선 두 번 누르기가 확대로 먹히니 한 번 누름 그대로.
   */
  openOnDoubleClick?: boolean;
  /**
   * 두 번 눌러 여는 표에서 **한 번 누름**(#300 — 운행차량편성 줄을 고르면 아래 내역 탭). 폰은 한 번 누름이 열기라 안 부른다.
   * 칸 복사(`copy`)와 같이 일어난다(복사 칸을 눌러도 줄이 골라진다).
   */
  onRowSelect?: (row: Row) => void;
  /** 줄 끌어 고르기용(#203) — 화면이 고름 상태를 갖고, 표는 줄 위 누름·지나감만 알려 준다. */
  onRowMouseDown?: (row: Row, e: ReactMouseEvent) => void;
  onRowMouseEnter?: (row: Row) => void;
  /** 선택된 행을 표시하기 위한 판정. 상세 패널이 열려 있을 때 어느 줄인지 보이게 한다. */
  activeRow?: (row: Row) => boolean;
  /** 행 바탕색(분류 색 등). 선택된 행(`activeRow`)이 이긴다. */
  rowClass?: (row: Row) => string;
  /** 총계 줄(맨 아래 고정). 서버가 **필터 전체**에 대해 낸 값만 넣을 것. */
  total?: TotalRow | null;
  /** 소계 줄들. `groupOf` 와 짝이다 — 둘 중 하나만 주면 소계를 그리지 않는다. */
  subtotals?: SubtotalRow[] | null;
  /** 행이 어느 소계 그룹에 속하는지. 목록이 **그 키로 정렬돼 있어야** 소계가 제자리에 선다. */
  groupOf?: (row: Row) => string;
  /** 접어 보기(#489) — `FoldSpec`. 주면 보이는 줄만 그리기(virt)는 끈다(줄 높이가 섞인다). */
  folds?: FoldSpec | null;
  /**
   * 이 표의 저장 이름. 주면 **칼럼 폭·표시 여부·순서(머리글 끌기)를 기억**한다(이 브라우저에만, `lib/tableCols.ts`).
   * ⚠️ **탭까지 구분해서** 지을 것 — 한 화면이 탭마다 다른 칼럼을 쓰면 키가 겹쳐 서로를 덮어쓴다.
   * 안 주면 조절 UI 자체가 안 뜬다(미리보기·업로드 결과처럼 일회성 표).
   */
  tableId?: string;
  /**
   * 총계 줄 위치. 기본은 아래(`bottom`).
   * `top` 이면 **머리글 바로 밑**에 붙는다 — 행이 수백 줄이면 아래까지 내려가야 총계가 보인다.
   * ⚠️ 소계는 위로 못 올린다. 그룹 **사이**에 있어야 어느 묶음의 합인지 읽히기 때문이다.
   */
  totalAt?: 'top' | 'bottom';
  /**
   * 정렬·칼럼별 검색(2026-08-02). **셋을 다 주면 켜진다** — 주는 화면만 쓰고 나머지는 그대로다.
   *
   * 🔴 정렬·필터는 **서버가** 한다. 화면에서 하면 "이 페이지만" 정렬·필터한 것이 되는데
   *    목록은 한 페이지(최대 200행)뿐이라 사용자는 그 차이를 볼 수 없다.
   *    그래서 여기서는 **누른 사실만 위로 올리고** 실제 조회는 부모가 서버에 맡긴다.
   */
  sort?: { key: string; dir: 'asc' | 'desc' } | null;
  onSort?: (key: string) => void;
  filters?: Record<string, string>;
  onFilter?: (key: string, value: string) => void;
  /**
   * 숫자 칸(`align: 'right'`)을 끌어서 고르면 **고른 칸의 합계**를 화면 아래에 띄운다(#145 · 엑셀 상태 표시줄처럼).
   * 같은 칸 세로로 끌기 · Ctrl 누르고 누르면 칸 더하기/빼기(다른 칸도) · Shift 누르면 거기까지 · Esc·표 밖 누르면 풀림.
   * 줄 누르기(`onRowClick`)가 있는 표에도 안전하다 — 끈 뒤의 클릭은 줄 누르기로 안 넘긴다.
   */
  dragSum?: boolean;
  /** `copy` 칸을 복사한 뒤 알림(칼럼 이름·칸 수·복사한 글자). 실패면 `error`. */
  onCopy?: (info: { label: string; count: number; text: string; error?: string }) => void;
  /** 위 검색창 글자 — 칸에서 걸린 글자를 노랗게(#188). 칼럼별 검색 값은 그 칸에만 같이 강조한다. */
  highlight?: string;
  /**
   * 「엑셀 받기」(#217 — 버스인처럼 통계용 원자료). 칸 = 지금 보이는 칸·순서 그대로 · 줄 = `loadAll`(지금 조건 전부 —
   * 쪽 나누기와 무관) 이 있으면 그것, 없으면 지금 받아 둔 줄. 금액·숫자 칸은 숫자로 넣는다(엑셀에서 바로 더할 수 있게).
   */
  exportXlsx?: { name: string; loadAll?: () => Promise<Row[]> };
}) {
  const { prefs, isHidden, toggleHidden, setWidth, setOrder, reset, customized, savedTick, saveNow } = useTablePrefs(tableId);
  // 머리글을 끌어 바꾼 순서(#146). 숨긴 칼럼도 자리를 지킨다 — 다시 켜면 있던 곳에 돌아온다.
  const ordered = tableId ? applyOrder(columns, prefs.order) : columns;
  // 전부 숨기면 표가 사라져 되돌릴 수도 없다 — 최소 하나는 남긴다.
  const shown = tableId ? ordered.filter((c) => !isHidden(c.key)) : columns;
  const phone = usePhone();
  // dragSum 이 아닌 표의 `copy` 칸 = 누르고 Ctrl+C 로 그 값 복사(#241 · #250) · onCopy 가 없으면 화면 아래 공용 알림(#446).
  const copyCell = (c: Column, row: Row, e: ReactMouseEvent) => {
    if (!c.copy) return;
    const text = c.copy(row).trim();
    if (!text) return;
    if (!dbl) e.stopPropagation(); // 한 번 누름으로 줄이 열리는 표(폰)에선 복사만
    const label = typeof c.label === 'string' ? c.label : c.key;
    // #250 — 누르면 고르기만, Ctrl+C 로 복사.
    armCopy(text, e.currentTarget as HTMLElement,
      onCopy ? (r) => onCopy({ label, count: 1, text, error: r.ok ? undefined : r.error }) : undefined, true, label);
  };
  // #446(10-30 정여진 「전 화면 두 번 눌러 열기로 통일」) — 기본이 두 번 누르기. 한 번 누름으로 열어야 하는 표만 false.
  const dbl = openOnDoubleClick !== false && !phone;
  const base = shown.length > 0 ? shown : ordered;
  // ── 머리글 끌어서 순서 바꾸기(#146) ── PC 에서 `tableId` 가 있을 때만(폰은 고정 칸 규칙이 따로 있다).
  const canMove = !!tableId && !phone;
  const [colDrag, setColDrag] = useState<{ key: string; over?: string; after?: boolean } | null>(null);
  /** 폭 손잡이(오른쪽 4px)를 잡은 끌기는 순서 바꾸기가 아니다 — 누른 자리를 기억해 dragstart 에서 거른다. */
  const grabOk = useRef(true);
  const cols = phone
    ? [...base.filter((c) => c.phone === 'pin'), ...base.filter((c) => c.phone !== 'pin' && c.phone !== 'hide')]
    : base;
  /** 폰에서 왼쪽 고정 칸 — 배경을 칸마다 칠해야 밀려 오는 칸이 비치지 않는다. 너무 긴 이름은 화면 40% 에서 자른다. */
  const pinCls = (c: Column, bg = 'bg-surface') =>
    phone && c.phone === 'pin' ? `sticky left-0 z-[5] ${bg} max-w-[40vw] overflow-hidden text-ellipsis shadow-[1px_0_0_rgb(229_231_235)]` : '';

  const bySub = subtotals && groupOf ? new Map(subtotals.map((s) => [s.key, s])) : null;
  const byFold = folds ? new Map(folds.rows.map((s) => [s.key, s])) : null;
  const foldKey = (row: Row | undefined) => {
    if (!row || !folds || !byFold) return null;
    const k = folds.of(row);
    return k && byFold.has(k) ? k : null;
  };

  // ── 끌어서 합계(#145) ── 칸 키 = `줄번호:칼럼키`. 0·빈 칸은 고르지 않는다(세는 수가 보이는 숫자와 맞게).
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  const [dragging, setDragging] = useState(false);
  const pick = useRef<{ col: string; anchor: number; base: Set<string>; moved: boolean; live: boolean }>(
    { col: '', anchor: -1, base: new Set(), moved: false, live: false });
  const numAt = (i: number, key: string) => {
    const v = rows[i]?.[key];
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) && n !== 0 ? n : null;
  };
  const colBy = new Map(cols.map((c) => [c.key, c]));
  /** 복사·고르기에 쓰는 칸 글자. `copy` 칸은 그 값, 숫자 칸은 숫자 그대로. 빈 값 = 고르지 않음. */
  const textAt = (i: number, key: string) => {
    const c = colBy.get(key);
    const row = rows[i];
    if (!row) return '';
    if (c?.copy) return c.copy(row);
    const n = numAt(i, key);
    return n == null ? '' : String(n);
  };
  const rangeKeys = (key: string, a: number, b: number) => {
    const out: string[] = [];
    // #298 후속 — 빈 칸도 고른다(칠해지고 복사 때 자리를 지킨다). 빈 칸을 빼면 그 칸만 빠진 줄이 생기고, 칸 전체가 비면 칸째 사라진다.
    for (let i = Math.min(a, b); i <= Math.max(a, b); i++) out.push(`${i}:${key}`);
    return out;
  };
  /** 고른 칸을 엑셀처럼 — 줄은 줄바꿈, 같은 줄 여러 칸은 탭(칸 순서대로).
   *  🔴 고른 칸의 합집합을 줄마다 같은 순서로 낸다 — 빈 칸(입금 줄의 「지급」 등)을 빼면 붙여 넣을 때 칸이 밀린다(#298). */
  const pickedText = (keys: Set<string>) => {
    const byRow = new Map<number, Set<string>>();
    const usedCols = new Set<string>();
    for (const k of keys) {
      const cut = k.indexOf(':');
      const i = Number(k.slice(0, cut));
      if (!byRow.has(i)) byRow.set(i, new Set());
      byRow.get(i)!.add(k.slice(cut + 1));
      usedCols.add(k.slice(cut + 1));
    }
    const order = cols.filter((c) => usedCols.has(c.key));
    return [...byRow.keys()].sort((a, b) => a - b)
      .map((i) => order.map((c) => (byRow.get(i)!.has(c.key) ? textAt(i, c.key) : '')).join('\t'))
      .join('\n');
  };
  const copyPicked = (col: string, keys: Set<string>) => {
    const c = colBy.get(col);
    if (!c?.copy || keys.size === 0) return;
    if ([...keys].every((k) => { const cut = k.indexOf(':'); return textAt(Number(k.slice(0, cut)), k.slice(cut + 1)) === ''; })) return; // 고른 칸이 전부 빈 칸
    const text = pickedText(keys);
    const label = typeof c.label === 'string' ? c.label : c.key;
    // #250 — 끌어 고른 칸은 칠해져 있고, Ctrl+C 로 복사(표 밖을 누르면 풀린다).
    armCopy(text, boxRef.current, (r) => (onCopy
      ? onCopy({ label, count: keys.size, text, error: r.ok ? undefined : r.error })
      : copyToast(r.ok ? copyMessage(label, text, keys.size) : `복사하지 못했습니다: ${r.error}`)), false);
  };
  // 놓는 순간(window mouseup) 최신 고름으로 복사하려고 — 이벤트 리스너는 첫 렌더의 값을 붙들고 있다.
  const finish = useRef<() => void>(() => {});
  finish.current = () => {
    const p = pick.current;
    if (!p.live) return;
    copyPicked(p.col, p.moved ? pickedRef.current : new Set([`${p.anchor}:${p.col}`]));
  };
  // 다시 조회하면 줄 번호가 다른 줄을 가리킨다 — 풀어 둔다(부모가 매번 새 배열을 줘도 안 풀리게 길이·첫 줄로 본다).
  useEffect(() => { setPicked(new Set()); }, [rows.length, rows[0]]);
  useEffect(() => {
    if (!dragSum) return;
    const up = () => { finish.current(); pick.current.live = false; setDragging(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setPicked(new Set()); };
    const outside = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setPicked(new Set()); };
    window.addEventListener('mouseup', up);
    window.addEventListener('keydown', key);
    window.addEventListener('mousedown', outside);
    return () => { window.removeEventListener('mouseup', up); window.removeEventListener('keydown', key); window.removeEventListener('mousedown', outside); };
  }, [dragSum]);
  const sumOn = (c: Column) => Boolean(dragSum && (c.align === 'right' || c.copy));
  const cellDown = (i: number, c: Column, e: ReactMouseEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button,a,input,select,textarea')) return;
    const k = `${i}:${c.key}`;
    const p = pick.current;
    if (e.shiftKey && p.col === c.key && p.anchor >= 0) {
      e.preventDefault();
      const next = new Set([...(e.ctrlKey || e.metaKey ? picked : []), ...rangeKeys(c.key, p.anchor, i)]);
      setPicked(next); p.moved = true;
      copyPicked(c.key, next);
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const next = new Set(picked);
      if (next.has(k)) next.delete(k); else if (textAt(i, c.key) !== '') next.add(k);
      setPicked(next);
      pick.current = { col: c.key, anchor: i, base: next, moved: true, live: true };
      setDragging(true);
      return;
    }
    e.preventDefault(); // 끄는 동안 글자가 파랗게 잡히지 않게
    // 복사 칸은 한 번 누른 칸도 칠해 둔다(무엇을 복사했는지 보이게). 숫자만 있는 칸은 예전처럼 풀기.
    // 빈 칸에서도 끌기를 시작할 수 있다(#298 후속) — 한 번 누름만으로는 복사할 것이 없어 copyPicked 가 건너뛴다.
    setPicked(c.copy ? new Set([k]) : new Set());
    pick.current = { col: c.key, anchor: i, base: new Set(), moved: false, live: true };
    setDragging(true);
  };
  const cellEnter = (i: number, c: Column) => {
    const p = pick.current;
    if (!p.live || !sumOn(c)) return;
    if (!p.moved && i === p.anchor && c.key === p.col) return;
    p.moved = true;
    // 옆 칸까지 끌면 네모로(#172 후속 최여진 — 「내용」·「입금」 두 칸을 같이 끌어 엑셀처럼 복사).
    // 시작 칸과 지금 칸 사이의 고를 수 있는 칸 전부 × 줄 범위. 같은 줄 여러 칸은 탭으로 이어진다(pickedText).
    const a = cols.findIndex((x) => x.key === p.col);
    const b = cols.findIndex((x) => x.key === c.key);
    const span = a < 0 || b < 0 ? [c] : cols.slice(Math.min(a, b), Math.max(a, b) + 1).filter(sumOn);
    setPicked(new Set([...p.base, ...span.flatMap((x) => rangeKeys(x.key, p.anchor, i))]));
  };
  /** 끈 직후의 클릭은 줄 누르기로 넘기지 않는다. */
  const cellClick = (e: ReactMouseEvent) => { if (pick.current.moved && picked.size) e.stopPropagation(); };
  let pickedSum = 0;
  let pickedNums = 0;
  for (const k of picked) {
    const cut = k.indexOf(':');
    const key = k.slice(cut + 1);
    if (colBy.get(key)?.align !== 'right') continue; // 글자 칸(내용 등)은 합계에 안 넣는다
    const n = numAt(Number(k.slice(0, cut)), key);
    if (n != null) { pickedSum += Math.round(n * 100); pickedNums++; }
  }

  /**
   * 표를 **뷰포트 높이 안에 묶는다.**
   *
   * 🔴 왜: `overflow-x-auto` 만으로는 넘치는 칸을 볼 수 없다. 가로 스크롤바는 표 **맨 아래**에
   *    생기는데 50행짜리 표는 높이가 2,000px 라 스크롤바가 **화면 밖 1,155px 아래**에 있다
   *    (2026-09-16 사용자 신고 · 운행차량편성 실측: 표시폭 1,230 / 실제 1,498 → 268px 가 안 보이고
   *    `정산` 칼럼이 통째로 잘렸다). 사용자에게는 "잘렸는데 스크롤도 없다"로 보인다.
   *
   * 높이는 **박스가 놓인 자리**와 **아래에 붙은 쪽 넘김·합계 줄**을 재서 정한다(`useFitHeight` · #255 —
   * 예전엔 아래 줄을 안 빼 페이지가 넘쳤고, 쪽을 넘기려 내리면 틀 전체가 밀려 올라갔다).
   */
  const boxRef = useRef<HTMLDivElement>(null);
  // 행 수·칼럼이 바뀌면 위쪽 요소 높이도 달라질 수 있어 다시 잰다.
  const maxHeight = useFitHeight(boxRef, [rows.length, cols.length]);

  // 끌어 고르는 중 마우스가 표 위·아래 끝에 닿으면 표를 굴리고, 굴린 자리의 칸까지 이어서 고른다
  // (11-02 #288 후속 이현숙 「입금내역도 출납일보와 같은 방식」 — 출납일보 `CashBook.tsx` 와 같은 식 · dragSum 표 공통).
  const enterRef = useRef(cellEnter);
  enterRef.current = cellEnter;
  const colsRef = useRef(cols);
  colsRef.current = cols;
  useEffect(() => {
    if (!dragging || !dragSum) return;
    let x = 0; let y = 0; let last: string | null = null;
    const move = (e: MouseEvent) => { x = e.clientX; y = e.clientY; };
    window.addEventListener('mousemove', move);
    const timer = window.setInterval(() => {
      const box = boxRef.current;
      if (!box || (!x && !y) || !pick.current.live) return;
      const rc = box.getBoundingClientRect();
      const head = (box.querySelector('thead') as HTMLElement | null)?.offsetHeight ?? 0;
      const top = rc.top + head; const edge = 40;
      let dy = 0;
      if (y > rc.bottom - edge) dy = Math.min(60, (y - (rc.bottom - edge)) / 2 + 6);
      else if (y < top + edge) dy = -Math.min(60, ((top + edge) - y) / 2 + 6);
      if (!dy) return;
      const before = box.scrollTop;
      box.scrollTop += dy;
      if (box.scrollTop === before) return;
      const px = Math.min(Math.max(x, rc.left + 4), rc.right - 4);
      const py = Math.min(Math.max(y, top + 2), rc.bottom - 4);
      const key = document.elementFromPoint(px, py)?.closest('td[data-cell]')?.getAttribute('data-cell');
      if (!key || key === last) return;
      last = key;
      const cut = key.indexOf(':');
      const c = colsRef.current.find((v) => v.key === key.slice(cut + 1));
      if (c) enterRef.current(Number(key.slice(0, cut)), c);
    }, 30);
    return () => { window.removeEventListener('mousemove', move); window.clearInterval(timer); };
  }, [dragging, dragSum]);

  // ── 보이는 줄만 그리기(#241) — 범위 재료: 박스 스크롤·높이, 머리글 높이, 줄 높이(그린 줄 평균).
  const virt = !bySub && !byFold && rows.length > VIRT_MIN;
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const [scroll, setScroll] = useState({ top: 0, h: 900 });
  const [rowH, setRowH] = useState(33);
  const [headH, setHeadH] = useState(0);
  const scrollRaf = useRef(0);
  /**
   * 🔴 줄 높이는 **스크롤 한 번(또는 새 자료)에 한 번만** 잰다(10-16 #295 정여진 — 재무추가를 내리다 화면이 통째로 사라짐).
   *    매 렌더마다 재면 줄 높이가 섞인 구간에서 「높이 바뀜 → 그릴 범위 바뀜 → 다른 줄들의 평균 → 높이 바뀜」이 돌아
   *    React 가 `Maximum update depth exceeded` 로 표를 내리고 오류 경계가 없어 화면 전체가 흰색이 됐다(하니스 재현 35,700px).
   */
  const measureOk = useRef(true);
  const measuredRows = useRef<Row[] | null>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !virt) return;
    const read = () => { scrollRaf.current = 0; measureOk.current = true; setScroll({ top: el.scrollTop, h: el.clientHeight }); };
    const onScroll = () => { if (!scrollRaf.current) scrollRaf.current = requestAnimationFrame(read); };
    read();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current);
      scrollRaf.current = 0;
    };
  }, [virt, maxHeight]);
  useLayoutEffect(() => {
    if (!virt) return;
    const h = theadRef.current?.offsetHeight ?? 0;
    if (Math.abs(h - headH) > 0.5) setHeadH(h);
    if (measuredRows.current !== rows) { measuredRows.current = rows; measureOk.current = true; }
    if (!measureOk.current) return;
    const trs = Array.from(tbodyRef.current?.children ?? []).filter((t) => !t.hasAttribute('aria-hidden')) as HTMLElement[];
    if (!trs.length) return;
    measureOk.current = false;
    const avg = trs.reduce((n, t) => n + t.offsetHeight, 0) / trs.length;
    if (avg > 0 && Math.abs(avg - rowH) > 0.5) setRowH(avg);
  });

  // 폭을 하나라도 정했으면 `fixed` 로 바꾼다 — `auto` 에서는 지정 폭이 내용에 밀려 안 먹는다.
  // 반대로 아무것도 안 정했으면 `auto` 가 낫다(내용에 맞춰 알아서 잡힌다).
  // 🔴 폰에선 PC 에서 저장한 폭을 **쓰지 않는다**(2026-09-30 way 캡처 — 폭이 있으면 `table-fixed` 가 13칸을 390px 에
  //    욱여넣어 「20/09」 두 줄·「1...」 잘림이 났다). 폰은 칸마다 내용대로 한 줄, 표를 옆으로 밀어서 본다. 저장값은 그대로 남는다.
  const anyWidth = !phone && Object.keys(prefs.width).length > 0;

  /**
   * 폭을 좁혔을 때 셀이 **옆 칸을 덮지 않게** 한다.
   *
   * 🔴 `.td` 는 `whitespace-nowrap` 인데 `table-fixed` 에서는 넘치는 내용이 잘리지 않고
   *    그대로 흘러나와 **옆 칸 글자와 겹쳐 보인다**(2026-07-31 사용자 신고).
   *    `auto` 일 때는 칸이 내용에 맞춰 늘어나 안 생기던 문제라, 폭을 정한 순간에만 나타난다.
   *
   * 처방은 컬럼 성격에 따라 다르다:
   *  · 글자 칸 → **줄바꿈을 허용**한다. 좁혀도 값 전체가 보이는 게 낫다.
   *    한국어는 `break-keep` 으로 어절 중간을 쪼개지 않는다.
   *  · 숫자 칸(`align: right`) → 줄바꿈하면 자릿수가 흐트러진다. 한 줄로 두고 넘치면 자른다.
   * 둘 다 `overflow-hidden` 을 깔아 **어떤 경우에도 옆 칸을 침범하지 않게** 한다.
   */
  const cellClip = (c: Column) =>
    !anyWidth ? '' : c.align === 'right' ? 'overflow-hidden text-ellipsis' : 'overflow-hidden whitespace-normal break-keep';
  //
  // 🔴 **이 선언은 `totalCells`·`body` 보다 위에 있어야 한다.** 아래에 두었다가
  //    `Cannot access 'N' before initialization`(TDZ)으로 **정산 화면이 백지가 됐다**(2026-07-31 prod).
  //    `tsc` 는 못 잡는다 — 쓰는 자리가 `cols.map(...)` **콜백 안**이라 실행 시점을 알 수 없어
  //    use-before-declaration 검사가 비켜 간다. 순서로만 막을 수 있다.

  /** 합계 줄 하나. 첫 칸에 이름, 나머지는 `values` 에 있는 컬럼만 채운다. */
  const totalCells = (t: TotalRow, fallback: string, bg = '') =>
    cols.map((c, i) => (
      <td key={c.key} className={`td ${c.align === 'right' ? 'num' : ''} ${cellClip(c)} ${bg} ${pinCls(c, bg || 'bg-surface')} font-semibold`}>
        {i === 0 ? (
          <span>
            {t.label ?? fallback}
            <span className="ml-1.5 font-normal text-ink-faint">{t.count.toLocaleString('ko-KR')}건</span>
          </span>
        ) : c.totalRender ? (
          c.totalRender(t.values)
        ) : c.key in t.values ? (
          money(t.values[c.key])
        ) : (
          ''
        )}
      </td>
    ));

  /**
   * 보이는 행이 전체보다 적으면 **그렇다고 말한다.** 안 그러면 총계와 눈으로 더한 값이
   * 안 맞을 때 어느 쪽이 틀렸는지 아무도 못 푼다. 위·아래 어디에 붙든 같은 문구를 쓴다.
   */
  const totalNote = (t: TotalRow, bg = '') => (
    <td className={`td pt-0 text-xs text-ink-faint ${bg}`} colSpan={cols.length}>
      총계는 <b>검색·필터에 걸린 전체 {t.count.toLocaleString('ko-KR')}건</b> 기준입니다 —
      지금 화면에는 {rows.length.toLocaleString('ko-KR')}건만 보입니다.
    </td>
  );

  const hlTerms = highlightTerms(highlight);
  const cellOf = (c: Column, row: Row): ReactNode => {
    const v = c.render ? c.render(row) : dash(row[c.key]);
    const terms = [...hlTerms, ...highlightTerms(filters?.[c.key])];
    return terms.length ? markTerms(v, terms) : v;
  };
  const body: ReactNode[] = [];
  /**
   * 🔴 줄이 많으면(「전체」 쪽 크기 · 수천 줄) **보이는 줄만** 그린다(10-14 #241 정여진 — 기준정보 「응답 없음」).
   *    logPerf 실측: 그분 PC 에서 기준정보 한 번 그리기 23초 · 타이머 끝 렌더 41~112초. 처음 넣은 「400줄씩 붙이기」는
   *    붙일 때마다 그린 줄 전부를 다시 그려 일이 오히려 늘었다(5천 줄 = 12번). 월정기(#123 useBoard)와 같은 방식:
   *    박스 스크롤 위치 → 그릴 범위, 위·아래는 빈 줄 높이로 채운다. 소계가 끼는 표(정산)는 줄 높이가 섞여 그대로 둔다.
   *    소계·총계·합계·엑셀은 rows 전체 기준 그대로(그리는 것만 줄인다).
   */
  const vStart = virt ? Math.min(Math.max(0, Math.floor((scroll.top - headH) / rowH) - VIRT_OVER), Math.max(0, rows.length - 1)) : 0;
  const vEnd = virt ? Math.min(rows.length, vStart + Math.ceil(scroll.h / rowH) + VIRT_OVER * 2) : rows.length;
  if (virt && vStart > 0) {
    body.push(<tr key="vtop" aria-hidden style={{ height: vStart * rowH }}><td colSpan={cols.length} style={{ padding: 0, border: 0 }} /></tr>);
  }
  rows.slice(vStart, vEnd).forEach((row, j) => {
    const i = vStart + j;
    // 접어 보기(#489) — 묶음이 시작되는 자리에 머리 줄 · 접혀 있으면 멤버 줄은 안 그린다.
    const fk = foldKey(row);
    if (fk && folds) {
      const head = byFold!.get(fk)!;
      const opened = folds.open.has(fk);
      if (foldKey(rows[i - 1]) !== fk) {
        const nameAt = folds.labelKey && cols.some((c, ci) => ci > 0 && c.key === folds.labelKey) ? folds.labelKey : null;
        // 펼침 표시는 이름 바로 앞(+/− · 요청 문구·완료 안내 그대로) — 맨 앞 칸에 따로 두면 이름과 떨어져 무엇을 여는지 안 읽힌다(11-03 way 캡처).
        const chevron = <span className="w-3 shrink-0 text-center font-bold text-violet-600">{opened ? '−' : '+'}</span>;
        const name = (
          <span className="inline-flex min-w-0 items-center gap-1 whitespace-nowrap">
            {chevron}<span className="truncate">{head.label ?? head.key}</span>{folds.badge?.(head)}
          </span>
        );
        body.push(
          <tr key={`f${i}`} className="cursor-pointer bg-violet-50 hover:bg-violet-100" onClick={() => folds.toggle(fk)}
              title={opened ? '누르면 접습니다' : '누르면 묶음 안 접수가 펼쳐집니다'}>
            {cols.map((c, ci) => (
              <td key={c.key} className={`td ${c.align === 'right' ? 'num' : ''} ${cellClip(c)} ${pinCls(c, 'bg-violet-50')} font-semibold`}>
                {ci === 0 ? (!nameAt && name) : c.key === nameAt ? name : c.totalRender ? c.totalRender(head.values) : c.key in head.values ? money(head.values[c.key]) : ''}
              </td>
            ))}
          </tr>,
        );
      }
      if (!opened) return;
    }
    body.push(
      <tr
        key={`r${i}`}
        onClick={onRowClick && !dbl ? () => onRowClick(row) : dbl && onRowSelect ? () => onRowSelect(row) : undefined}
        onDoubleClick={onRowClick && dbl ? () => { window.getSelection()?.removeAllRanges(); onRowClick(row); } : undefined}
        onMouseDown={onRowMouseDown ? (e) => onRowMouseDown(row, e) : undefined}
        onMouseEnter={onRowMouseEnter ? () => onRowMouseEnter(row) : undefined}
        className={`${onRowClick ? 'cursor-pointer' : ''} ${
          // #356 — 고른 줄은 눈에 띄는 옅은 노랑(예전 brand-50 은 흰 바탕과 거의 같아 안 보였다).
          activeRow?.(row) ? 'bg-amber-100' : `${rowClass?.(row) ?? ''} hover:bg-canvas`
        }`}
      >
        {cols.map((c) => (
          <td key={c.key}
              className={`td ${c.align === 'right' ? 'num' : ''} ${cellClip(c)} ${pinCls(c, activeRow?.(row) ? 'bg-amber-100' : 'bg-surface')} ${
                sumOn(c) ? 'cursor-cell' : ''} ${picked.has(`${i}:${c.key}`) ? '!bg-sky-200/70' : ''}`}
              data-cell={sumOn(c) ? `${i}:${c.key}` : undefined}
              onMouseDown={sumOn(c) ? (e) => cellDown(i, c, e) : undefined}
              onMouseEnter={sumOn(c) ? () => cellEnter(i, c) : undefined}
              onClick={sumOn(c) ? cellClick : c.copy ? (e) => copyCell(c, row, e) : undefined}
              title={c.copy ? COPY_HINT : undefined}>
            {cellOf(c, row)}
          </td>
        ))}
      </tr>,
    );
    // 그룹이 바뀌는 자리(=마지막 행 뒤)에 그 그룹의 소계를 끼운다.
    if (bySub && groupOf) {
      const here = groupOf(row);
      const nextRow = rows[i + 1];
      const next = nextRow ? groupOf(nextRow) : null;
      const sub = bySub.get(here);
      if (sub && here !== next) {
        body.push(
          <tr key={`s${i}`} className="bg-canvas/70">
            {totalCells(sub, '소계')}
          </tr>,
        );
      }
    }
  });
  if (virt && vEnd < rows.length) {
    body.push(<tr key="vbot" aria-hidden style={{ height: (rows.length - vEnd) * rowH }}><td colSpan={cols.length} style={{ padding: 0, border: 0 }} /></tr>);
  }


  return (
    <div className="space-y-1">
      {tableId ? (
        <ColumnBar
          columns={ordered}
          isHidden={isHidden}
          toggleHidden={toggleHidden}
          reset={reset}
          customized={customized}
          savedTick={savedTick}
          move={(from, to, after) => setOrder(moveKey(ordered.map((x) => x.key), from, to, after))}
          onSave={saveNow}
          shownCount={base.length} // 폰에서 뺀 칸(phone: hide)은 사용자가 숨긴 게 아니다 — 세지 않는다
          extra={exportXlsx && <ExportButton spec={exportXlsx} columns={base} rows={rows} />}
        />
      ) : exportXlsx ? (
        <div className="flex items-center gap-2 text-xs"><ExportButton spec={exportXlsx} columns={base} rows={rows} /></div>
      ) : null}
      <TopScrollbar target={boxRef} />
      <div ref={boxRef} className="card overflow-auto" style={{ maxHeight }}>
        {/* 칸 사이 세로줄(`grid-lines` · #215) — 칸이 많은 표에서 어느 값이 어느 칸인지 눈으로 따라가게. */}
        <table className={`grid-lines w-full ${anyWidth ? 'table-fixed' : ''} ${dragging ? 'select-none' : ''}`}>
          {anyWidth && (
            <colgroup>
              {cols.map((c) => (
                <col key={c.key} style={colWidthStyle(prefs, c.key)} />
              ))}
            </colgroup>
          )}
        {/*
          머리글·필터줄·상단총계를 **박스 위에 붙인다**(sticky). 50행을 내려도 어느 칸인지 잃지 않는다.
          🔴 배경은 **셀(`th`·`td`)마다** 준다. `thead` 에 주면 computed 값은 흰색인데
             **sticky 상태에서 칠해지지 않아** 밑으로 지나가는 행이 머리글 글자와 겹쳐 보인다
             (2026-09-16 실측 — tablelab 스크린샷으로 확인).
        */}
        <thead ref={theadRef} className="sticky top-0 z-20">
          <tr>
            {cols.map((c) => (
              <th
                  key={c.key}
                  className={`th relative bg-surface ${c.align === 'right' ? 'text-right' : ''} ${pinCls(c)} ${
                    colDrag?.key === c.key ? 'opacity-40' : ''
                  } ${
                    colDrag?.over === c.key && colDrag.key !== c.key
                      ? colDrag.after ? 'shadow-[inset_-3px_0_0_rgb(37_99_235)]' : 'shadow-[inset_3px_0_0_rgb(37_99_235)]'
                      : ''
                  }`}
                  // 좁히면 머리글도 잘린다 — 어느 칸인지 잃지 않게 전체 이름을 툴팁으로 남긴다.
                  title={typeof c.label === 'string' ? (canMove ? `${c.label} · 끌어서 순서 바꾸기` : c.label) : undefined}
                  // 정렬 누르기는 그대로다 — 브라우저 끌기는 마우스가 움직여야 시작된다.
                  draggable={canMove}
                  onPointerDownCapture={(e) => { grabOk.current = !(e.target as Element).closest('[role="separator"]'); }}
                  onDragStart={(e) => {
                    if (!canMove || !grabOk.current) { e.preventDefault(); return; }
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', c.key); // 파이어폭스는 데이터가 없으면 끌기를 안 시작한다
                    setColDrag({ key: c.key });
                  }}
                  onDragOver={(e) => {
                    if (!colDrag) return;
                    e.preventDefault();
                    const r = e.currentTarget.getBoundingClientRect();
                    const after = e.clientX > r.left + r.width / 2;
                    if (colDrag.over !== c.key || colDrag.after !== after) setColDrag({ ...colDrag, over: c.key, after });
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (colDrag?.over) setOrder(moveKey(ordered.map((x) => x.key), colDrag.key, c.key, !!colDrag.after));
                    setColDrag(null);
                  }}
                  onDragEnd={() => setColDrag(null)}
                >
                {onSort && !c.noSort ? (
                  <button
                    type="button"
                    className="flex w-full items-center gap-1 text-left font-semibold hover:text-brand-700"
                    onClick={() => onSort(c.key)}
                    title="눌러서 오름차순 ↔ 내림차순"
                  >
                    <span className={anyWidth ? 'block truncate' : undefined}>{c.label}</span>
                    {/* 지금 정렬 중인 칸만 화살표를 진하게 — 나머지는 "누를 수 있다"만 흐리게 알린다. */}
                    <span className={`inline-block w-3 shrink-0 text-center text-[10px] ${sort?.key === c.key ? 'text-brand-700' : 'text-ink-faint/40'}`}>
                      {sort?.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                ) : (
                  <span className={anyWidth ? 'block truncate' : undefined}>{c.label}</span>
                )}
                {tableId && !phone && <ResizeHandle onResize={(px) => setWidth(c.key, px)} />}
              </th>
            ))}
          </tr>
          {/* 칼럼별 검색 줄. 머리글 안에 두면 칸 폭이 정확히 맞고 스크롤해도 같이 움직인다. */}
          {onFilter && (
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={`th bg-surface py-1 ${pinCls(c)}`}>
                  <input
                    className="field h-7 w-full px-1.5 py-0 text-xs font-normal"
                    placeholder="검색"
                    value={filters?.[c.key] ?? ''}
                    onChange={(e) => onFilter(c.key, e.target.value)}
                  />
                </th>
              ))}
            </tr>
          )}
          {/*
            상단 총계는 **머리글과 같은 `thead` 안에** 둔다 — 칸 폭이 머리글과 정확히 맞고,
            sticky 가 머리글·필터줄·총계를 **한 덩어리로** 붙여 준다.
            ⚠️ 예전처럼 `thead` 를 하나 더 열면 두 덩어리가 각자 `top:0` 에 붙어 **겹친다**.
          */}
          {total && totalAt === 'top' && (
            <>
              <tr className="border-b-2 border-line">{totalCells(total, '총계', 'bg-canvas')}</tr>
              {total.count > rows.length && <tr>{totalNote(total, 'bg-canvas')}</tr>}
            </>
          )}
        </thead>
        <tbody ref={tbodyRef}>
          {body}
          {/* 첫 로딩에 몸통이 비면 「결과 없음」으로 읽힌다 — 표 안에서 불러오는 중임을 알린다. */}
          {rows.length === 0 && (
            <tr>
              <td className="td text-ink-faint" colSpan={cols.length}>
                {loading ? '불러오는 중…' : '조회 결과가 없습니다.'}
              </td>
            </tr>
          )}
        </tbody>
        {total && totalAt === 'bottom' && (
          <tfoot className="border-t-2 border-line bg-canvas">
            <tr>{totalCells(total, '총계')}</tr>
            {total.count > rows.length && <tr>{totalNote(total)}</tr>}
          </tfoot>
        )}
        </table>
      </div>
      {/* 고른 칸 합계 — 화면에 떠 있어 표가 밀리지 않는다(밀리면 마우스 밑 칸이 바뀐다 · 출납일보 #111). */}
      {picked.size > 0 && (
        <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-lg border border-sky-300 bg-sky-50 px-4 py-2 text-sm shadow-lg">
          <span>{picked.size.toLocaleString('ko-KR')}칸 고름</span>
          {pickedNums > 0 && <span className="tabular-nums">합계 <b>{money(pickedSum / 100)}</b></span>}
          <button type="button" className="btn-ghost py-0.5 text-xs" onClick={() => setPicked(new Set())}>풀기 (Esc)</button>
        </div>
      )}
    </div>
  );
}

/**
 * 칼럼 폭을 끄는 손잡이. 머리글 오른쪽 끝 4px 를 잡고 끈다.
 *
 * 왜 `pointer` 이벤트인가: 마우스·터치·펜을 한 코드로 받고, `setPointerCapture` 로
 * **표 밖으로 나가도 드래그가 이어진다**(mousemove 만 쓰면 커서가 표를 벗어나는 순간 끊긴다).
 * 드래그 중에는 `select-none` 으로 텍스트 선택을 막는다 — 안 막으면 머리글이 파랗게 잡힌다.
 */
export function ResizeHandle({ onResize }: { onResize: (px: number) => void }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <span
      ref={ref}
      role="separator"
      aria-orientation="vertical"
      title="끌어서 칼럼 폭 조절"
      className={`absolute inset-y-0 right-0 w-1.5 cursor-col-resize select-none ${
        dragging ? 'bg-brand-400' : 'hover:bg-line'
      }`}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation(); // 머리글 클릭(정렬 등)으로 새지 않게
        const th = ref.current?.closest('th');
        if (!th) return;
        const startX = e.clientX;
        const startW = th.getBoundingClientRect().width;
        setDragging(true);
        e.currentTarget.setPointerCapture(e.pointerId);

        const move = (ev: PointerEvent) => onResize(Math.max(MIN_COL_WIDTH, startW + ev.clientX - startX));
        const up = () => {
          setDragging(false);
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      }}
    />
  );
}

/**
 * 칼럼 고르기 줄. **숨긴 칼럼이 있으면 그 수를 늘 드러낸다** — 조용히 숨기면
 * "그 항목이 없어졌다"는 신고가 되고, 원인이 자기 설정이라는 걸 아무도 못 찾는다.
 */
export function ColumnBar({
  columns,
  isHidden,
  toggleHidden,
  reset,
  customized,
  savedTick = 0,
  move,
  onSave,
  shownCount,
  extra,
}: {
  columns: Column[];
  isHidden: (k: string) => boolean;
  toggleHidden: (k: string) => void;
  reset: () => void;
  customized: boolean;
  /** `useTablePrefs().savedTick` — 바뀔 때마다 「항목저장」을 잠깐 띄운다(#215). */
  savedTick?: number;
  /** 항목관리 창 안에서 칸 순서 옮기기(#291 — 머리글 끌기와 같은 저장). 없으면 순서 손잡이를 안 그린다. */
  move?: (from: string, to: string, after: boolean) => void;
  /** 「항목저장」 버튼(#291). 없으면 버튼을 안 그린다. */
  onSave?: () => void;
  shownCount: number;
  /** 줄 끝에 붙일 것(엑셀 받기 등). */
  extra?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  /** 항목관리 창에서 끌고 있는 칸(#291). */
  const [chipDrag, setChipDrag] = useState<string | null>(null);
  const hiddenCount = columns.length - shownCount;
  // 칼럼을 숨기거나 폭·순서를 바꾸면 바로 저장된다(이 브라우저). 저장됐다는 걸 **눈으로** 보여 준다 —
  // 레거시는 「항목저장」을 눌러야 남아서, 말없이 저장되면 「저장이 안 됐나」 하고 다시 고친다(#215).
  const [savedShown, setSavedShown] = useState(false);
  useEffect(() => {
    if (!savedTick) return;
    setSavedShown(true);
    const t = setTimeout(() => setSavedShown(false), 3000);
    return () => clearTimeout(t);
  }, [savedTick]);

  return (
    <div className="flex items-center gap-2 text-xs">
      <button className="btn-ghost py-0.5 text-xs" onClick={() => setOpen((v) => !v)}>
        항목관리 {shownCount}/{columns.length}
        {hiddenCount > 0 && <span className="ml-1 text-amber-700">· {hiddenCount}개 숨김</span>}
      </button>
      {customized && (
        <button className="btn-ghost py-0.5 text-xs text-ink-faint" onClick={reset}>
          기본으로
        </button>
      )}
      {savedShown && <span className="font-medium text-emerald-700">✓ 항목저장</span>}
      {extra}
      {open && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-line bg-surface px-2 py-1">
          {move && <span className="text-ink-faint">이름을 끌어 순서를 바꾸고, 체크로 보이기·숨기기</span>}
          {columns.map((c, i) => {
            const off = isHidden(c.key);
            // 마지막 하나까지 끄면 표가 사라진다 — 그 체크는 못 끄게 막는다.
            // `required` 칸도 같다(그 칸이 없으면 나머지 칸의 뜻이 무너진다).
            const last = c.required === true || (!off && shownCount <= 1);
            return (
              <label
                key={c.key}
                className={`flex items-center gap-1 rounded px-0.5 ${last ? 'opacity-40' : 'cursor-pointer'} ${
                  chipDrag === c.key ? 'bg-sky-100' : ''
                }`}
                // #291 — 창 안에서 칸 순서 옮기기(머리글 끌기와 같은 저장 · 놓은 칸 앞으로).
                draggable={!!move}
                onDragStart={(e) => {
                  if (!move) return;
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', c.key);
                  setChipDrag(c.key);
                }}
                onDragOver={(e) => { if (chipDrag) e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (move && chipDrag && chipDrag !== c.key) {
                    const from = columns.findIndex((x) => x.key === chipDrag);
                    move(chipDrag, c.key, from < i);
                  }
                  setChipDrag(null);
                }}
                onDragEnd={() => setChipDrag(null)}
                title={
                  c.required === true
                    ? '이 표의 기준 칼럼이라 숨길 수 없습니다.'
                    : last
                      ? '마지막 칼럼은 숨길 수 없습니다.'
                      : undefined
                }
              >
                <input type="checkbox" checked={!off} disabled={last} onChange={() => toggleHidden(c.key)} />
                <span className={off ? 'text-ink-faint line-through' : ''}>
                  {typeof c.label === 'string' ? c.label : c.key}
                </span>
              </label>
            );
          })}
          {onSave && (
            <button
              className="btn-primary py-0.5 text-xs"
              onClick={() => {
                onSave();
                setOpen(false);
              }}
            >
              항목저장
            </button>
          )}
          <button className="btn-ghost py-0.5 text-xs" onClick={() => setOpen(false)}>
            닫기
          </button>
        </div>
      )}
    </div>
  );
}

/** 「전체」 = 서버 `paging()` 상한과 같은 값(#96). 그보다 많으면 「다음」으로 이어 본다. */
export const PAGE_ALL = 5000;

/** 엑셀 칸 값 — 숫자 칸은 숫자로, 화면이 글자로 만드는 칸은 그 글자로(빈 표시 「—」는 빈칸). */
export function exportCell(c: Column, r: Row): string | number {
  const raw = r[c.key];
  if (c.align === 'right' && raw !== null && raw !== undefined && raw !== '') {
    const n = Number(String(raw).replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  const shown = c.render?.(r);
  if (typeof shown === 'string' || typeof shown === 'number') return shown === '—' ? '' : shown;
  if (raw === null || raw === undefined || typeof raw === 'object') return '';
  if (typeof raw === 'boolean') return raw ? 'Y' : 'N';
  return raw as string | number;
}

/** 쪽마다 끝까지 받기(서버 상한 = PAGE_ALL). `fetchPage` 는 limit·offset 만 바꿔 같은 조건으로 부른다. */
export async function loadAllPages(fetchPage: (limit: number, offset: number) => Promise<Row[]>): Promise<Row[]> {
  const out: Row[] = [];
  for (let offset = 0; ; offset += PAGE_ALL) {
    const got = await fetchPage(PAGE_ALL, offset);
    out.push(...got);
    if (got.length < PAGE_ALL) return out;
  }
}

function ExportButton({ spec, columns, rows }: { spec: { name: string; loadAll?: () => Promise<Row[]> }; columns: Column[]; rows: Row[] }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function run() {
    setBusy(true); setErr(null);
    try {
      const all = spec.loadAll ? await spec.loadAll() : rows;
      const cols = columns.filter((c) => typeof c.label === 'string' && c.label.trim() !== '');
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      const sheet = XLSX.utils.aoa_to_sheet([cols.map((c) => c.label as string), ...all.map((r) => cols.map((c) => exportCell(c, r)))]);
      XLSX.utils.book_append_sheet(wb, sheet, spec.name.slice(0, 31));
      const d = new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
      XLSX.writeFile(wb, `${spec.name}_${d}.xlsx`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button type="button" className="btn-ghost py-0.5 text-xs" disabled={busy} onClick={() => void run()} title="지금 조건의 줄 전부를 보이는 칸 그대로 엑셀로">
        {busy ? '받는 중…' : '엑셀 받기'}
      </button>
      {err && <span className="text-red-700">{err}</span>}
    </>
  );
}

/**
 * 목록 한 쪽 줄 수(#96 — 50줄씩 넘기기보다 전체를 보고 싶다) — 화면마다 따로, 이 브라우저에 기억한다.
 * 바꾸면 그 화면 목록 로딩 deps 에 들어가 다시 불러온다(쪽은 Pager 가 1쪽으로 되돌린다).
 */
export function usePageSize(key: string, base: number): [number, (n: number) => void] {
  const storeKey = `pageSize:${key}`;
  const [size, setSize] = useState(() => {
    try {
      const v = Number(localStorage.getItem(storeKey));
      return v === base || v === 200 || v === PAGE_ALL ? v : base;
    } catch { return base; }
  });
  return [size, (n: number) => {
    setSize(n);
    try { localStorage.setItem(storeKey, String(n)); } catch { /* 저장 못 해도 이번 화면엔 적용 */ }
  }];
}

export function Pager({
  page,
  setPage,
  rowCount,
  pageSize,
  loading,
  setPageSize,
  baseSize = 50,
}: {
  page: number;
  setPage: (f: (p: number) => number) => void;
  rowCount: number;
  pageSize: number;
  loading: boolean;
  /** 주면 「50줄 · 200줄 · 전체」 고르기가 붙는다(`usePageSize`). */
  setPageSize?: (n: number) => void;
  baseSize?: number;
}) {
  const sizes = [...new Set([baseSize, 200, PAGE_ALL])].sort((a, b) => a - b);
  return (
    <div className="flex items-center gap-3 text-sm">
      {setPageSize && (
        <select className="field w-auto py-1 text-xs" value={pageSize} disabled={loading} title="한 쪽에 보여 줄 줄 수"
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(() => 0); }}>
          {sizes.map((n) => <option key={n} value={n}>{n === PAGE_ALL ? '전체' : `${n}줄씩`}</option>)}
        </select>
      )}
      <button className="btn-ghost" disabled={page === 0 || loading} onClick={() => setPage((p) => p - 1)}>
        이전
      </button>
      <span className="text-ink-soft">{page + 1} 쪽</span>
      <button
        className="btn-ghost"
        disabled={rowCount < pageSize || loading}
        onClick={() => setPage((p) => p + 1)}
      >
        다음
      </button>
      {loading && <span className="text-ink-faint">불러오는 중…</span>}
    </div>
  );
}

/**
 * 목록 로딩 공통. `deps` 가 바뀌면 다시 부른다.
 * 앞선 요청이 늦게 도착해 화면을 덮어쓰지 않게 취소 플래그를 둔다.
 * 🔴 `load` 안에서 rows 말고 다른 상태(합계·요약)를 직접 넣을 땐 `alive()` 가 참일 때만 —
 *    안 그러면 옛 조건의 늦은 응답이 그 상태만 덮는다(입금 내역 「112건」 ↔ 표 3줄 · 10-27).
 */
export function useList(
  load: (alive: () => boolean) => Promise<ListResult<Row> | ListResultLike>,
  deps: readonly unknown[],
): {
  rows: Row[];
  loading: boolean;
  error: string | null;
  /** 서버가 낸 총계·소계. 목록과 **같은 응답**에서 오므로 필터가 어긋날 수 없다. */
  total: TotalRow | null;
  subtotals: SubtotalRow[];
} {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState<TotalRow | null>(null);
  const [subtotals, setSubtotals] = useState<SubtotalRow[]>([]);
  // 마운트하면 곧바로 부르므로 true 로 시작 — false 면 첫 화면에 「결과 없음」이 한 번 스친다.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 화면 탭 「↻ 새로고침」 — 같은 조건으로 다시 읽는다(`lib/pageRefresh.ts`).
  const refreshTick = usePageRefresh();
  // 머리줄 「담당자」를 바꾸면 같은 조건으로 다시 읽는다(api.ts 가 고른 값을 실어 보낸다 · 11-02).
  const ownerPick = useOwnerPick();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load(() => !cancelled)
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        const withTotals = r as { total?: Record<string, unknown> | null; subtotals?: Record<string, unknown>[] };
        setTotal(toTotal(withTotals.total));
        setSubtotals(toSubtotals(withTotals.subtotals));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refreshTick, ownerPick]);

  return { rows, loading, error, total, subtotals };
}

/** 목록이 아닌 응답도 useList 로 받을 수 있게 — rows 만 있으면 된다. */
export type ListResultLike = { rows: Row[] };

export function ErrorBox({ message }: { message: string }) {
  // 서버 문구의 줄바꿈(겹친 운행 목록 #233 등)을 그대로 보인다.
  return <div className="card whitespace-pre-line border-red-200 bg-red-50 p-4 text-sm text-red-700">{message}</div>;
}
